import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { projectApi } from '@/modules/project/api'
import { loadProjectDetailBundle } from '@/modules/project/composables/useProjectDetailLoader'
import { deriveProjectFolderAccess, type ProjectFolderAccess } from '@/modules/project/api/project-access'
import type { WorkItem } from '@/modules/project/api/work-item'
import type {
  Project,
  ProjectListItem,
  ProjectQueryParams,
  ProjectCreateRequest,
  ProjectUpdateRequest,
  ProjectListResponse
} from '@/modules/project/api'
import { ProjectType, ProjectStatus } from '@/api/common-types'
export type { Project, ProjectQueryParams } from '@/modules/project/api'
export { ProjectType, ProjectStatus } from '@/api/common-types'

function currentUserId(): number | null {
  try {
    const raw = localStorage.getItem('userInfo')
    if (!raw) return null
    const parsed = JSON.parse(raw) as { id?: number }
    return typeof parsed.id === 'number' ? parsed.id : null
  } catch {
    return null
  }
}

// 项目统计信息（保持向后兼容）
export interface ProjectStats {
  total: number
  video_count: number
  document_count: number
  draft_count: number
  pending_count: number
  processing_count: number
  completed_count: number
  failed_count: number
}

// 项目状态管理
export const useProjectStore = defineStore('project', () => {
  // 状态
  const projects = ref<ProjectListItem[]>([])
  const currentProject = ref<Project | null>(null)
  const loading = ref(false)
  const stats = ref<ProjectStats>({
    total: 0,
    video_count: 0,
    document_count: 0,
    draft_count: 0,
    pending_count: 0,
    processing_count: 0,
    completed_count: 0,
    failed_count: 0
  })

  // 分页信息
  const pagination = ref({
    current: 1,
    pageSize: 20,
    total: 0
  })

  // 筛选条件
  const filters = ref<ProjectQueryParams>({})

  const detailWorkItems = ref<WorkItem[]>([])
  const detailStatusCounts = ref<Record<string, number>>({})
  const detailAccess = ref<ProjectFolderAccess>(deriveProjectFolderAccess(null, null))

  // 缓存
  const projectCache = ref<Map<number, Project>>(new Map())
  const cacheExpiry = ref<Map<number, number>>(new Map())
  const CACHE_DURATION = 5 * 60 * 1000 // 5分钟缓存

  // 计算属性
  const videoProjects = computed(() =>
    projects.value.filter(p => p.project_type === ProjectType.VIDEO)
  )

  const documentProjects = computed(() =>
    projects.value.filter(p => p.project_type === ProjectType.DOCUMENT)
  )

  const myProjects = computed(() => {
    const userId = currentUserId()
    return projects.value.filter(p => p.creator_id === userId)
  })

  const draftProjects = computed(() =>
    projects.value.filter(p => p.status === ProjectStatus.DRAFT)
  )

  const activeProjects = computed(() =>
    projects.value.filter(p =>
      p.status === ProjectStatus.PENDING || p.status === ProjectStatus.PROCESSING
    )
  )

  const completedProjects = computed(() =>
    projects.value.filter(p => p.status === ProjectStatus.COMPLETED)
  )

  // 缓存管理
  const isCacheValid = (projectId: number): boolean => {
    const expiry = cacheExpiry.value.get(projectId)
    return expiry ? Date.now() < expiry : false
  }

  const setCacheItem = (project: Project) => {
    projectCache.value.set(project.id, project)
    cacheExpiry.value.set(project.id, Date.now() + CACHE_DURATION)
  }

  const getCacheItem = (projectId: number): Project | null => {
    if (isCacheValid(projectId)) {
      return projectCache.value.get(projectId) || null
    }
    return null
  }

  const clearCache = () => {
    projectCache.value.clear()
    cacheExpiry.value.clear()
  }

  // API 操作
  const fetchProjects = async (params?: ProjectQueryParams) => {
    loading.value = true
    try {
      const keepDefined = (obj: Record<string, unknown>) =>
        Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ''))

      const queryParams: any = {
        page: pagination.value.current,
        page_size: pagination.value.pageSize,
        ...keepDefined(filters.value as Record<string, unknown>),
        ...(params ? keepDefined(params as Record<string, unknown>) : {}),
      }

      const response = await projectApi.getProjects(queryParams) as ProjectListResponse
      projects.value = response.items
      pagination.value.total = response.total

      // 更新缓存
      response.items.forEach((project: ProjectListItem) => {
        setCacheItem(project as Project)
      })

      return response
    } catch (error) {
      console.error('获取项目列表失败:', error)
      projects.value = []
      pagination.value.total = 0
      throw error
    } finally {
      loading.value = false
    }
  }

  const getProject = async (id: number): Promise<Project | null> => {
    // 先检查缓存
    const cached = getCacheItem(id)
    if (cached) {
      currentProject.value = cached
      return cached
    }

    try {
      loading.value = true
      const response = await projectApi.getProject(id)
      const project = response as Project

      currentProject.value = project
      setCacheItem(project)

      return project
    } catch (error) {
      console.error('Failed to fetch project:', error)
      return null
    } finally {
      loading.value = false
    }
  }

  const createProject = async (projectData: ProjectCreateRequest) => {
    try {
      loading.value = true
      const response = await projectApi.createProject(projectData)
      const newProject = response as Project

      projects.value.unshift(newProject as ProjectListItem)
      setCacheItem(newProject)

      return newProject
    } finally {
      loading.value = false
    }
  }

  // 保持向后兼容性：仅创建文件夹，条目请走 workItemApi
  const createVideoProject = async (projectData: { title: string }) => {
    return createProject({ title: projectData.title })
  }

  const createDocumentProject = async (projectData: { title: string }) => {
    return createProject({ title: projectData.title })
  }

  const updateProject = async (id: number, updateData: ProjectUpdateRequest) => {
    try {
      loading.value = true
      const response = await projectApi.updateProject(id, updateData)
      const updatedProject = response as Project

      // 更新列表中的项目
      const index = projects.value.findIndex(p => p.id === id)
      if (index !== -1) {
        projects.value[index] = updatedProject as ProjectListItem
      }

      // 更新当前项目
      if (currentProject.value?.id === id) {
        currentProject.value = updatedProject
      }

      // 更新缓存
      setCacheItem(updatedProject)

      return updatedProject
    } finally {
      loading.value = false
    }
  }

  const deleteProject = async (id: number) => {
    try {
      loading.value = true
      await projectApi.deleteProject(id)

      // 从列表中移除
      projects.value = projects.value.filter(p => p.id !== id)

      // 清除当前项目
      if (currentProject.value?.id === id) {
        currentProject.value = null
      }

      // 清除缓存
      projectCache.value.delete(id)
      cacheExpiry.value.delete(id)

    } finally {
      loading.value = false
    }
  }

  const fetchProjectStats = async () => {
    console.warn(
      'fetchProjectStats 已废弃：统计请从 loadProjectDetail 的 detailStatusCounts 或 dashboard API 获取',
    )
    return stats.value
  }

  const loadProjectDetail = async (projectId: number, userId?: number | null) => {
    loading.value = true
    try {
      const resolvedUserId = userId ?? currentUserId()
      const bundle = await loadProjectDetailBundle(projectId, resolvedUserId)
      currentProject.value = bundle.project
      if (bundle.project) {
        setCacheItem(bundle.project)
      }
      detailWorkItems.value = bundle.workItems
      detailStatusCounts.value = bundle.statusCounts
      detailAccess.value = bundle.access
      return bundle
    } catch (error) {
      console.error('加载项目详情失败:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  // 筛选和搜索
  const setFilters = (newFilters: ProjectQueryParams) => {
    filters.value = { ...filters.value, ...newFilters }
    pagination.value.current = 1 // 重置到第一页
  }

  const clearFilters = () => {
    filters.value = {}
    pagination.value.current = 1
  }

  const searchProjects = async (keyword: string) => {
    setFilters({ search_keyword: keyword })
    return await fetchProjects()
  }

  // 分页
  const setPagination = (page: number, pageSize?: number) => {
    pagination.value.current = page
    if (pageSize) {
      pagination.value.pageSize = pageSize
    }
  }

  // 状态管理
  const setCurrentProject = (project: Project | null) => {
    currentProject.value = project
  }

  const updateProjectStatus = async (id: number, status: ProjectStatus) => {
    try {
      loading.value = true
      const response = await projectApi.updateProjectStatus(id, { status })
      const updatedProject = response as Project

      // 更新列表中的项目
      const index = projects.value.findIndex(p => p.id === id)
      if (index !== -1) {
        projects.value[index] = updatedProject as ProjectListItem
      }

      // 更新当前项目
      if (currentProject.value?.id === id) {
        currentProject.value = updatedProject
      }

      // 更新缓存
      setCacheItem(updatedProject)

      return updatedProject
    } finally {
      loading.value = false
    }
  }

  const updateProjectProgress = async (_id: number, _progress: number) => {
    throw new Error(
      'updateProjectProgress 已废弃：进度在 WorkItem / Task 上，请使用 workItemApi。',
    )
  }

  // 批量操作：文件夹无“批量状态”；删除改为逐个 deleteProject
  const batchDeleteProjects = async (ids: number[]) => {
    try {
      loading.value = true
      for (const id of ids) {
        await projectApi.deleteProject(id)
      }
      projects.value = projects.value.filter(p => !ids.includes(p.id))
      ids.forEach(id => {
        projectCache.value.delete(id)
        cacheExpiry.value.delete(id)
      })
    } finally {
      loading.value = false
    }
  }

  const batchUpdateProjectStatus = async (_ids: number[], _status: ProjectStatus) => {
    throw new Error(
      'batchUpdateProjectStatus 已废弃：文件夹无 status，请按 WorkItem/Task 状态操作。',
    )
  }

  // 持久化状态
  const saveFiltersToStorage = () => {
    localStorage.setItem('project-filters', JSON.stringify(filters.value))
  }

  const loadFiltersFromStorage = () => {
    const saved = localStorage.getItem('project-filters')
    if (saved) {
      filters.value = JSON.parse(saved)
    }
  }

  const savePaginationToStorage = () => {
    localStorage.setItem('project-pagination', JSON.stringify(pagination.value))
  }

  const loadPaginationFromStorage = () => {
    const saved = localStorage.getItem('project-pagination')
    if (saved) {
      const savedPagination = JSON.parse(saved)
      pagination.value = { ...pagination.value, ...savedPagination }
    }
  }

  // 初始化
  const initialize = () => {
    loadFiltersFromStorage()
    loadPaginationFromStorage()
  }

  return {
    // 状态
    projects,
    currentProject,
    loading,
    stats,
    pagination,
    filters,
    detailWorkItems,
    detailStatusCounts,
    detailAccess,

    // 计算属性
    videoProjects,
    documentProjects,
    myProjects,
    draftProjects,
    activeProjects,
    completedProjects,

    // 方法
    fetchProjects,
    getProject,
    createProject,
    createVideoProject,
    createDocumentProject,
    updateProject,
    deleteProject,
    fetchProjectStats,
    loadProjectDetail,
    setFilters,
    clearFilters,
    searchProjects,
    setPagination,
    setCurrentProject,
    updateProjectStatus,
    updateProjectProgress,
    batchDeleteProjects,
    batchUpdateProjectStatus,
    clearCache,
    saveFiltersToStorage,
    loadFiltersFromStorage,
    savePaginationToStorage,
    loadPaginationFromStorage,
    initialize
  }
})

